import { Injectable } from '@nestjs/common';
import { CategorySummary, CreateCategoryRequest, UpdateCategoryRequest } from '@cart/shared-types';
import { CATEGORIES } from './categories.data';

export abstract class CategoriesRepository {
  abstract list(parentId?: string): Promise<CategorySummary[]>;
  abstract findById(categoryId: string): Promise<CategorySummary | undefined>;
  abstract create(input: CreateCategoryRequest): Promise<CategorySummary>;
  abstract update(categoryId: string, input: UpdateCategoryRequest): Promise<CategorySummary | undefined>;
  abstract delete(categoryId: string): Promise<boolean>;
  abstract existsBySlug(slug: string): Promise<boolean>;
}

@Injectable()
export class InMemoryCategoriesRepository implements CategoriesRepository {
  private readonly categories = CATEGORIES.map((category) => ({ ...category }));

  async list(parentId?: string): Promise<CategorySummary[]> {
    let items = this.categories;
    if (parentId === 'root') items = items.filter((c) => c.parentId === null);
    else if (parentId !== undefined) items = items.filter((c) => c.parentId === parentId);
    return [...items].sort((a, b) => a.name.localeCompare(b.name));
  }

  async findById(categoryId: string): Promise<CategorySummary | undefined> {
    const category = this.categories.find((c) => c.id === categoryId);

    return category ? { ...category } : undefined;
  }

  async create(input: CreateCategoryRequest): Promise<CategorySummary> {
    const parentLevel = input.parentId
      ? (this.categories.find((c) => c.id === input.parentId)?.level ?? 0)
      : -1;

    const category: CategorySummary = {
      id: `cat-${input.slug.trim().toLowerCase()}`,
      name: input.name.trim(),
      slug: input.slug.trim().toLowerCase(),
      parentId: input.parentId ?? null,
      level: parentLevel + 1,
      productCount: 0,
      totalProductCount: 0,
      createdAt: new Date().toISOString(),
    };

    this.categories.push(category);

    return category;
  }

  async update(categoryId: string, input: UpdateCategoryRequest): Promise<CategorySummary | undefined> {
    const category = this.categories.find((c) => c.id === categoryId);

    if (!category) {
      return undefined;
    }

    if (input.name !== undefined) category.name = input.name.trim();
    if ('parentId' in input) category.parentId = input.parentId ?? null;
    if (input.level !== undefined) category.level = input.level;

    return { ...category };
  }

  async delete(categoryId: string): Promise<boolean> {
    const index = this.categories.findIndex((c) => c.id === categoryId);

    if (index === -1) {
      return false;
    }

    this.categories.splice(index, 1);

    return true;
  }

  async existsBySlug(slug: string): Promise<boolean> {
    return this.categories.some((category) => category.slug === slug.trim().toLowerCase());
  }
}
